import { queryOptions } from "@tanstack/react-query";
import * as v from "valibot";

import { LeakedCode } from "./_models/leakedCode";
import { Order, OrdersUsingCodeWithinWeek } from "./_models/ordersUsingCodeWithinWeek";
import { codeUsageReader, leakedCodeReader } from "./_runtime";
import { CodeUsageReader } from "./codeUsageReader/service";
import { LeakedCodeReader } from "./leakedCodeReader/service";

export const LeakedCodeDetails = v.object({
  leakedCode: v.object({
    ...LeakedCode.entries,
    weeklyUsages: v.array(OrdersUsingCodeWithinWeek),
  }),
});
export type LeakedCodeDetails = v.InferOutput<typeof LeakedCodeDetails>;

const getWeekStart = (date: Date): Date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getWeekEnd = (date: Date): Date => {
  const d = new Date(date);
  d.setDate(d.getDate() + 6);
  d.setHours(23, 59, 59, 999);
  return d;
};

const generateWeeks = ({
  leakedAt,
  weeksAfter,
  weeksBefore,
}: {
  leakedAt: Date;
  weeksAfter: number;
  weeksBefore: number;
}) => {
  const weeks: { weekEnd: Date; weekStart: Date }[] = [];
  const leakWeekStart = getWeekStart(leakedAt);

  for (let i = -weeksBefore; i < weeksAfter; i++) {
    const weekStart = new Date(leakWeekStart);
    weekStart.setDate(weekStart.getDate() + i * 7);
    weeks.push({
      weekEnd: getWeekEnd(weekStart),
      weekStart,
    });
  }

  return weeks;
};

const groupOrdersByWeek = ({
  leakedAt,
  orders,
  weeksAfter,
  weeksBefore,
}: {
  leakedAt: Date;
  orders: Order[];
  weeksAfter: number;
  weeksBefore: number;
}): OrdersUsingCodeWithinWeek[] => {
  const weeksSurroundingLeak = generateWeeks({
    leakedAt,
    weeksAfter,
    weeksBefore,
  });

  return weeksSurroundingLeak.map(({ weekEnd, weekStart }) => ({
    orders: orders.filter((order) => order.createdAt >= weekStart && order.createdAt <= weekEnd),
    weekEnd,
    weekStart,
  }));
};

export const fetchLeakedCodeDetails =
  ({ codeUsageReader, leakedCodeReader }: { codeUsageReader: CodeUsageReader; leakedCodeReader: LeakedCodeReader }) =>
  async ({ id }: { id: string }) => {
    const leakedCode = await leakedCodeReader.byId(id);

    const WEEKS_TO_FETCH = 8;
    const nWeeksAgoStart = new Date(leakedCode.leakedAt.getTime() - WEEKS_TO_FETCH * 7 * 24 * 60 * 60 * 1000);
    const nWeeksFromNowEnd = new Date(leakedCode.leakedAt.getTime() + WEEKS_TO_FETCH * 7 * 24 * 60 * 60 * 1000);

    const orders = await codeUsageReader.getOrdersUsingCodeWithinDateRange({
      code: leakedCode.code,
      dateRange: { end: nWeeksFromNowEnd, start: nWeeksAgoStart },
      shopId: leakedCode.shopId,
    });

    const weeklyUsages = groupOrdersByWeek({
      leakedAt: leakedCode.leakedAt,
      orders,
      weeksAfter: WEEKS_TO_FETCH,
      weeksBefore: WEEKS_TO_FETCH,
    });

    return v.parse(LeakedCodeDetails, {
      leakedCode: {
        ...leakedCode,
        weeklyUsages,
      },
    });
  };

export const LeakedCodeDetailsQuery = ({ id }: { id: string | undefined }) =>
  queryOptions({
    enabled: !!id,
    queryFn: () => fetchLeakedCodeDetails({ codeUsageReader, leakedCodeReader })({ id: id! }),
    queryKey: ["leakedCodeDetails", id],
  });
